import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle2, AlertCircle, LogOut, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

/**
 * EmailVerificationGate
 * Wraps protected pages/features.
 * If user is not authenticated, requires sign in before continuing.
 * If user is logged in but hasn't verified email, shows verification prompt.
 * Admins and Google sign-in users (auto-verified) pass through immediately.
 */
export const EmailVerificationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isEmailVerified, isSuperAdmin, resendVerificationEmail, signOut, user, loading } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  // Still restoring session from local persistence — render clean spinner rather than flashing gate
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-[#094426] animate-spin" />
          <span className="text-xs text-slate-500 font-medium">
            {isFr ? 'Chargement de votre session...' : 'Verifying session...'}
          </span>
        </div>
      </div>
    );
  }

  // Not logged in — enforce authentication gate
  if (!isAuthenticated) {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/post-property';
    return (
      <div className="min-h-[75vh] bg-slate-50/60 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden p-6 sm:p-8 text-center space-y-5">
          <div className="w-16 h-16 bg-emerald-50 border-2 border-emerald-300 text-[#094426] rounded-2xl flex items-center justify-center mx-auto shadow-sm">
            <Lock className="w-8 h-8 text-[#094426]" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-black text-slate-900">
              {isFr ? 'Connexion Obligatoire' : 'Sign In Required'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              {isFr
                ? 'Pour des raisons de sécurité et de conformité, chaque utilisateur doit se connecter avant de publier une annonce ou d’accéder à son espace.'
                : 'For security and compliance purposes, each user must sign in before posting an ad or managing listings.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Link
              to={isFr ? `/fr/connexion?redirect=${encodeURIComponent(currentPath)}` : `/en/signin?redirect=${encodeURIComponent(currentPath)}`}
              className="w-full sm:w-auto flex-1 px-5 py-2.5 bg-[#094426] hover:bg-[#07361e] text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2"
            >
              <span>{isFr ? 'Se Connecter' : 'Sign In'}</span>
            </Link>
            <Link
              to={isFr ? `/fr/inscription?redirect=${encodeURIComponent(currentPath)}` : `/en/signup?redirect=${encodeURIComponent(currentPath)}`}
              className="w-full sm:w-auto flex-1 px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-2"
            >
              <span>{isFr ? 'Créer un Compte' : 'Create Account'}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Already verified, admin, or Google account (auto-verified) — pass through
  if (isEmailVerified || isSuperAdmin) return <>{children}</>;

  const handleResend = async () => {
    setIsResending(true);
    setResendMessage(null);
    setResendError(null);
    const res = await resendVerificationEmail();
    setIsResending(false);
    if (res.success) {
      setResendMessage(res.message);
    } else {
      setResendError(res.error || (isFr ? 'Échec de l\'envoi.' : 'Failed to send.'));
    }
  };

  const handleCheckVerification = async () => {
    // Reload will trigger onAuthStateChanged which updates isEmailVerified
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-slate-50/60 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500 text-white p-6 text-center">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-90" />
          <h1 className="text-xl font-bold">
            {isFr ? 'Vérifiez Votre Email' : 'Verify Your Email'}
          </h1>
          <p className="text-xs text-amber-100 mt-1.5">
            {isFr
              ? 'Un email de vérification a été envoyé à votre adresse'
              : 'A verification email has been sent to your address'}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Email display */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-center">
            <p className="text-xs text-slate-500 mb-1">
              {isFr ? 'Email envoyé à :' : 'Email sent to:'}
            </p>
            <p className="text-sm font-bold text-slate-900">
              {user?.email || '—'}
            </p>
          </div>

          {/* Instructions */}
          <div className="text-xs text-slate-600 leading-relaxed space-y-2">
            <p>
              {isFr
                ? '1. Ouvrez votre boîte de réception (vérifiez aussi les spams).'
                : '1. Open your email inbox (check spam/junk folder too).'}
            </p>
            <p>
              {isFr
                ? '2. Cliquez sur le lien de vérification dans l\'email.'
                : '2. Click the verification link in the email.'}
            </p>
            <p>
              {isFr
                ? '3. Revenez ici et cliquez "J\'ai Vérifié".'
                : '3. Come back here and click "I\'ve Verified".'}
            </p>
          </div>

          {/* Success message */}
          {resendMessage && (
            <div className="flex items-start gap-2.5 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>{resendMessage}</span>
            </div>
          )}

          {/* Error message */}
          {resendError && (
            <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <span>{resendError}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2.5 pt-2">
            <button
              onClick={handleCheckVerification}
              className="w-full bg-[#094426] hover:bg-[#07361e] text-white font-bold py-3 rounded-xl text-xs transition-colors shadow-sm flex items-center justify-center gap-2 cursor-pointer"
            >
              <CheckCircle2 className="w-4 h-4" />
              {isFr ? 'J\'ai Vérifié Mon Email' : 'I\'ve Verified My Email'}
            </button>

            <button
              onClick={handleResend}
              disabled={isResending}
              className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-2.5 rounded-xl text-xs transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {isResending ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              {isFr ? 'Renvoyer l\'Email de Vérification' : 'Resend Verification Email'}
            </button>

            <button
              onClick={signOut}
              className="w-full text-slate-400 hover:text-slate-600 font-medium py-2 text-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <LogOut className="w-3 h-3" />
              {isFr ? 'Se Déconnecter' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
